import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { HttpService } from './http.service';
import { API_CONFIG } from '../config/api.config';
import { ApiEnvelope } from '../models/api-response.model';

export const WORKSPACE_CUSTOMER_ID_KEY = 'customer_id';

export interface ProfileData {
  /** Workspace `customer.id` — use with `GET /v1/bookings?customerId=`. */
  customerId?: string;
  personalInfo: {
    firstName: string;
    middleName: string;
    lastName: string;
    profileImage?: string;
  };
  contactDetails: {
    email: string;
    alternateEmail: string;
    contactNumber: {
      countryCode: string;
      number: string;
    };
    address: string;
    state: string;
    city: string;
    pincode: string;
  };
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

@Injectable({
  providedIn: 'root',
})
export class ProfileService {
  constructor(private readonly httpService: HttpService) {}

  getProfile(): Observable<ApiResponse<ProfileData>> {
    return this.httpService.get<ApiEnvelope<ProfileData>>(API_CONFIG.profile.get).pipe(
      map((response) => ({
        success: response.status,
        data: response.data,
      })),
      tap((response) => this.cacheWorkspaceCustomerId(response.data?.customerId)),
    );
  }

  cacheWorkspaceCustomerId(customerId: string | undefined): void {
    const id = customerId?.trim();
    if (id && typeof localStorage !== 'undefined') {
      localStorage.setItem(WORKSPACE_CUSTOMER_ID_KEY, id);
    }
  }

  updateProfile(personal: any, contact: any): Observable<any> {
    const payload = this.buildUpdatePayload(personal, contact);
    return this.httpService.patch(API_CONFIG.profile.update, payload);
  }

  setPassword(newPassword: string): Observable<unknown> {
    return this.httpService.post(API_CONFIG.auth.setPassword, { newPassword });
  }

  private buildUpdatePayload(personal: any, contact: any) {
    return {
      personalInfo: {
        firstName: personal.firstName,
        middleName: personal.middleName,
        lastName: personal.lastName,
      },
      contactDetails: {
        alternateEmail: contact.alternateEmail,
        address: contact.address,
        state: contact.state,
        city: contact.city,
        pincode: contact.pincode,
      },
    };
  }
}
